import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Droplets } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Section } from '../templates/Section/Section';
import { Button } from '../atoms/Button/Button';
import { Badge } from '../atoms/Badge/Badge';
import { EmptyState } from '../atoms/EmptyState';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Alert } from '../atoms/Alert/Alert';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Dialog } from '../ui/Dialog/Dialog';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { KhSupplyForm } from './KhSupplyForm';
import { formatNumber } from './khSupplyFormat';
import {
  deleteKhSupplyConfig,
  getKhSupplyConfig,
  khSupplyQueryKey,
  khUsageQueryKey,
  type KhSupplyConfig,
} from '../../services/khSupplyService';
import './KhSupplySection.css';

export interface KhSupplySectionProps {
  aquariumId: string;
}

/** KH/Ca upkeep module on the dosing page: method + what is added in which strength. */
export function KhSupplySection({ aquariumId }: KhSupplySectionProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const configQuery = useQuery({
    queryKey: khSupplyQueryKey(aquariumId),
    queryFn: () => getKhSupplyConfig(aquariumId),
    enabled: aquariumId.length > 0,
  });

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: khSupplyQueryKey(aquariumId) });
    void queryClient.invalidateQueries({ queryKey: khUsageQueryKey(aquariumId) });
  }

  const deleteMutation = useMutation({
    mutationFn: () => deleteKhSupplyConfig(aquariumId),
    onSuccess: () => {
      refresh();
      setIsDeleteOpen(false);
    },
  });

  const config = configQuery.data ?? null;
  const n = (value: number) => formatNumber(value, i18n.language);

  function values(c: KhSupplyConfig): Array<{ term: string; value: string }> {
    if (c.method === 'calcium_reactor') {
      return [
        { term: t('dosing.khSupply.form.effluentFlow.label'), value: c.effluent_ml_per_min === null ? '—' : `${n(c.effluent_ml_per_min)} ml/min` },
        { term: t('dosing.khSupply.form.reactorHours.label'), value: `${n(c.reactor_hours_per_day ?? 24)} h` },
        { term: t('dosing.khSupply.form.effluentKh.label'), value: c.effluent_kh_dkh === null ? '—' : `${n(c.effluent_kh_dkh)} dKH` },
        { term: t('dosing.khSupply.form.effluentCa.label'), value: c.effluent_ca_mg_l === null ? '—' : `${n(c.effluent_ca_mg_l)} mg/l` },
      ];
    }
    return [
      {
        term: t(`dosing.khSupply.form.solutionMl.label_${c.method}`),
        value: c.kh_solution_ml_per_day === null ? '—' : `${n(c.kh_solution_ml_per_day)} ml`,
      },
      { term: t('dosing.khSupply.strength'), value: t(`dosing.khSupply.standardStrength.${c.method}`) },
    ];
  }

  return (
    <Section
      title={t('dosing.khSupply.title')}
      description={t('dosing.khSupply.description')}
      actions={config ? (
        <Button type="button" variant="secondary" onClick={() => setIsFormOpen(true)}>
          {t('dosing.khSupply.edit')}
        </Button>
      ) : undefined}
    >
      {configQuery.isLoading ? (
        <LiveRegion>
          <Text variant="muted">{t('dosing.khSupply.loading')}</Text>
        </LiveRegion>
      ) : configQuery.isError ? (
        <LoadErrorState
          title={t('dosing.khSupply.loadError')}
          onRetry={() => void configQuery.refetch()}
        />
      ) : config === null ? (
        <EmptyState
          icon={<Droplets size={48} strokeWidth={1.5} />}
          headline={t('dosing.khSupply.emptyHeadline')}
          description={t('dosing.khSupply.empty')}
          actionLabel={t('dosing.khSupply.setUp')}
          actionVariant="secondary"
          onAction={() => setIsFormOpen(true)}
          headingLevel={3}
        />
      ) : (
        <div className="kh-supply-section__card" data-testid="kh-supply-card">
          <Stack gap="4">
            <Inline gap="2" align="center" wrap>
              <Heading level={3}>{t(`dosing.khSupply.methods.${config.method}`)}</Heading>
              <Badge tone="info">{t('dosing.khSupply.active')}</Badge>
            </Inline>
            <dl className="kh-supply-section__values">
              {values(config).map((v) => (
                <div key={v.term} className="kh-supply-section__value">
                  <dt className="kh-supply-section__term">{v.term}</dt>
                  <dd className="kh-supply-section__definition">{v.value}</dd>
                </div>
              ))}
            </dl>
            <Text variant="muted" size="sm">{t('dosing.khSupply.usageHint')}</Text>
            <Inline gap="2" justify="end">
              <Button type="button" variant="ghost" onClick={() => setIsDeleteOpen(true)}>
                {t('dosing.khSupply.remove')}
              </Button>
            </Inline>
          </Stack>
        </div>
      )}

      <Dialog
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={config ? t('dosing.khSupply.form.editTitle') : t('dosing.khSupply.form.createTitle')}
        data-testid="kh-supply-dialog"
      >
        {isFormOpen && (
          <KhSupplyForm
            aquariumId={aquariumId}
            config={config}
            onSuccess={() => {
              refresh();
              setIsFormOpen(false);
            }}
            onCancel={() => setIsFormOpen(false)}
          />
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        title={t('dosing.khSupply.deleteConfirm.title')}
        message={t('dosing.khSupply.deleteConfirm.message')}
        confirmLabel={t('dosing.khSupply.deleteConfirm.confirm')}
        cancelLabel={t('dosing.deleteConfirm.cancel')}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => {
          deleteMutation.reset();
          setIsDeleteOpen(false);
        }}
        isConfirming={deleteMutation.isPending}
      >
        {deleteMutation.isError && (
          <Alert tone="danger">{t('dosing.khSupply.deleteError')}</Alert>
        )}
      </ConfirmDialog>
    </Section>
  );
}

export default KhSupplySection;
